import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, useCallback } from 'react'
import { formatStartDate, formatEndDate } from '@/utils/format'
import { notification } from 'antd'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'

import type { Unit } from '@/types/api'
import {
  fetchUnitsList,
  updateUnit,
  deleteUnit,
  getUnitById,
  LEVEL_STRING_TO_NUMBER,
  resetUnitPassword,
  sendUnitActivationMail,
} from '@/api/units.api'
import { buildColumns } from './columns'
import { UnitFilterPanel } from './UnitFilterPanel'
import type { UnitFilterValues } from './UnitFilterPanel'
import { UnitDetailDrawer } from './UnitDrawer'
import { UnitFormDrawer } from './UnitFormDrawer'
import { UnitInterfaceConfigDrawer } from './UnitUIConfigDrawer'
import { usePermission } from '@/hooks/use-permission'
import { exportUnit, useTableExport } from '@/api/dowload-excel.api'
import type { ExportPayload } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: UnitFilterValues = {
  keySearch: '',
  time: undefined,
  status: undefined,
}

export function UnitsPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const canCreate = usePermission('unit-create')
  const canView = usePermission('unit-view')
  const canUpdate = usePermission('unit-update')
  const canDelete = usePermission('unit-delete')
  const canResetPass = usePermission('unit-reset-pass')
  const canConfigTheme = usePermission('unit-update')
  const canExport = usePermission('unit-export')

  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<UnitFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<UnitFilterValues>(DEFAULT_FILTER)

  // States for Modals/Drawers
  const [viewingUnit, setViewingUnit] = useState<Unit | null>(null)
  const [configuringUnit, setConfiguringUnit] = useState<Unit | null>(null)
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'update'>('create')
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null)
  const [deletingUnit, setDeletingUnit] = useState<Unit | null>(null)
  const [togglingUnit, setTogglingUnit] = useState<Unit | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  const queryClient = useQueryClient()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['units', pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) => {
      return fetchUnitsList(
        pageIndex,
        pageSize,
        {
          keySearch: activeFilters.keySearch || undefined,
          status: activeFilters.status,
          fromDate: formatStartDate(activeFilters.time?.from) || undefined,
          toDate: formatEndDate(activeFilters.time?.to) || undefined,
        },
        signal,
      )
    },
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const { mutate: toggleStatus, isPending: isToggling } = useMutation({
    mutationFn: async (unit: Unit) => {
      const detail = await getUnitById(unit.id)

      let numericLevel = detail.level
      if (typeof detail.level === 'string') {
        numericLevel = LEVEL_STRING_TO_NUMBER[detail.level] || 2
      }

      return updateUnit(unit.id, {
        ...detail,
        level: numericLevel,
        status: unit.status === 1 ? 0 : 1,
      })
    },
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Cập nhật trạng thái đơn vị thành công!'
      notification.success({ title: successMessage })

      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
    onSettled: () => {
      setTogglingUnit(null)
    },
  })

  const { mutate: removeUnit, isPending: isRemoving } = useMutation({
    mutationFn: (unit: Unit) => deleteUnit(unit.id),
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Xóa đơn vị thành công!'
      notification.success({ title: successMessage })

      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
    onSettled: () => {
      setDeletingUnit(null)
    },
  })

  const { mutate: resetPassword } = useMutation({
    mutationFn: (unit: Unit) => resetUnitPassword(unit.id),
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Reset mật khẩu thành công!'
      notification.success({ title: successMessage })
    },
  })

  const { mutate: resendActivationMail } = useMutation({
    mutationFn: (unit: Unit) => sendUnitActivationMail(unit.id),
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Gửi lại mail kích hoạt thành công!'
      notification.success({ title: successMessage })
    },
  })

  const confirmDelete = (unit: Unit) => {
    setDeletingUnit(unit)
  }

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        canView,
        canUpdate,
        canDelete,
        canResetPass,
        canConfigTheme,
        onViewDetail: (unit) => setViewingUnit(unit),
        onUpdate: (unit) => {
          setFormMode('update')
          setEditingUnitId(unit.id)
          setIsFormDrawerOpen(true)
        },
        onConfigTheme: (unit) => setConfiguringUnit(unit),
        onDelete: confirmDelete,
        onToggleStatus: (unit) => setTogglingUnit(unit),
        onResetPassword: (unit) => resetPassword(unit),
        onResendActivationMail: (unit) => resendActivationMail(unit),
      }),
    [
      pageIndex,
      pageSize,
      canView,
      canUpdate,
      canDelete,
      canResetPass,
      canConfigTheme,
      resetPassword,
      resendActivationMail,
    ],
  )

  const rows = useMemo(() => data?.items ?? [], [data?.items])

  // Hàm gọi API xuất Excel cho Đơn vị
  const handleExportApi = useCallback(
    async (payload: ExportPayload) => {
      try {
        setIsExporting(true)
        await exportUnit(
          {
            page: pageIndex,
            size: pageSize,
            keySearch: activeFilters.keySearch || undefined,
            status: activeFilters.status,
            fromDate: formatStartDate(activeFilters.time?.from) || undefined,
            toDate: formatEndDate(activeFilters.time?.to) || undefined,
          },
          payload,
          'Danh_sach_don_vi',
        )
      } finally {
        setIsExporting(false)
      }
    },
    [pageIndex, pageSize, activeFilters],
  )

  const { handleExport } = useTableExport(
    'Danh sách đơn vị',
    columns,
    handleExportApi,
  )

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH ĐƠN VỊ" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title="DANH SÁCH ĐƠN VỊ"
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!(rows.length > 0)}
        isExporting={isExporting}
      >
        {canCreate && (
          <Button
            variant="primary"
            size="md"
            className="flex-1 gap-2 sm:flex-none h-10"
            onClick={() => {
              setFormMode('create')
              setEditingUnitId(null)
              setIsFormDrawerOpen(true)
            }}
          >
            <Plus size={16} />
            Thêm mới
          </Button>
        )}
      </PageHeader>

      {showFilter && (
        <UnitFilterPanel
          values={filter}
          onChange={setFilter}
          onSearch={() => {
            const trimmedFilter = {
              ...filter,
              keySearch: filter.keySearch.trim(),
            }
            setFilter(trimmedFilter)
            setActiveFilters(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={rows}
        tableOptions={{
          state: {
            pagination: { pageIndex, pageSize },
          },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={(p) =>
              setPagination((s) => ({ ...s, pageIndex: p - 1 }))
            }
            onPageSizeChange={(size) =>
              setPagination({ pageIndex: 0, pageSize: size })
            }
          />
        }
      />

      <UnitDetailDrawer
        unitId={viewingUnit?.id || null}
        onClose={() => setViewingUnit(null)}
        onUpdate={(unit) => {
          setViewingUnit(null)
          setFormMode('update')
          setEditingUnitId(unit.id)
          setIsFormDrawerOpen(true)
        }}
      />

      {configuringUnit && (
        <UnitInterfaceConfigDrawer
          unit={configuringUnit}
          onClose={() => setConfiguringUnit(null)}
        />
      )}

      {isFormDrawerOpen && (
        <UnitFormDrawer
          isOpen={isFormDrawerOpen}
          mode={formMode}
          unitId={editingUnitId}
          onClose={() => {
            setIsFormDrawerOpen(false)
            setEditingUnitId(null)
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!togglingUnit}
        title="Xác nhận"
        description={
          <>
            Bạn chắc chắn muốn{' '}
            {togglingUnit?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} đơn vị{' '}
            <strong>{togglingUnit?.name}</strong> này?
          </>
        }
        variant="info"
        isLoading={isToggling}
        onClose={() => setTogglingUnit(null)}
        onConfirm={() => togglingUnit && toggleStatus(togglingUnit)}
      />

      <ConfirmModal
        isOpen={!!deletingUnit}
        title="Xóa đơn vị"
        description={
          <>
            Khi đơn vị bị xóa các sơ đồ của đơn vị cũng sẽ bị xóa khỏi hệ thống.
            <br />
            Bạn chắc chắn muốn xóa đơn vị <strong>
              {deletingUnit?.name}
            </strong>{' '}
            này?
          </>
        }
        variant="danger"
        isLoading={isRemoving}
        onClose={() => setDeletingUnit(null)}
        onConfirm={() => deletingUnit && removeUnit(deletingUnit)}
      />
    </section>
  )
}
